import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Panel, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { ShieldCheck, UserMinus, Search } from "lucide-react";
import AdminRemovePlayerButton from "../components/AdminRemovePlayerButton";
import AdminResetPasswordButton from "../components/AdminResetPasswordButton";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [searchQ, setSearchQ] = useState("");

  const refresh = async () => {
    try {
      const params = searchQ.trim() ? `?q=${encodeURIComponent(searchQ.trim())}` : "";
      const { data } = await api.get(`/admin/users${params}`);
      setUsers(data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); refresh(); }, [searchQ]);

  const setRole = async (u, role) => {
    if (!window.confirm(
      role === "moderator"
        ? `Promote ${u.username} to moderator? They'll gain dispute-resolution and report powers.`
        : `Demote ${u.username} back to player?`
    )) return;
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role });
      toast.success(role === "moderator" ? `${u.username} promoted to moderator` : `${u.username} demoted to player`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="users-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] mb-1">// user management</div>
          <h2 className="font-heading text-3xl uppercase tracking-wider">Users & Roles</h2>
          <div className="font-mono text-[10px] text-[#71717A] mt-1">{users.length} shown</div>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); setSearchQ(q); }}
          className="flex items-center gap-2"
          data-testid="users-search-form"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-[#71717A]" />
            <input
              data-testid="users-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search username…"
              className="bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 pl-9 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#09090B] dark:text-[#F4F4F5] focus:outline-none focus:ring-1 focus:ring-[#7C3AED]"
            />
          </div>
          <button
            type="submit"
            data-testid="users-search-btn"
            className="border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      {loading ? (
        <TactLoader />
      ) : users.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm">[ NO USERS FOUND. ]</Panel>
      ) : (
        <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] overflow-x-auto" data-testid="users-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E4E4E7] dark:border-[#2A2A30]">
                <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">PLAYER</th>
                <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">GAME ID</th>
                <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">ROLE</th>
                <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">JOINED</th>
                <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const role = u.role || "player";
                const isAdmin = role === "admin";
                const isMod = role === "moderator";
                const isBanned = !!u.banned;
                return (
                  <tr key={u.id} className="border-b border-[#E4E4E7] dark:border-[#2A2A30]" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4 font-heading uppercase text-[#09090B] dark:text-[#F4F4F5]">{u.username}</td>
                    <td className="py-3 px-4 font-mono text-xs text-[#71717A]">{u.game_id || "—"}</td>
                    <td className="py-3 px-4">
                      <span className={`font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 border ${
                        isAdmin ? "text-[#E11D48] border-[#E11D48]"
                          : isMod ? "text-[#7C3AED] border-[#7C3AED]"
                          : "text-[#71717A] border-[#E4E4E7] dark:border-[#2A2A30]"
                      }`}>
                        {role}
                      </span>
                      {isBanned && <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 border text-[#E11D48] border-[#E11D48]">BANNED</span>}
                    </td>
                    <td className="py-3 px-4 font-mono text-[10px] text-[#71717A]">{fmt(u.created_at)}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-2 justify-end">
                        {isAdmin ? (
                          <span className="font-mono text-[10px] text-[#71717A]">— protected —</span>
                        ) : isMod ? (
                          <button
                            data-testid={`demote-${u.id}`}
                            onClick={() => setRole(u, "player")}
                            className="inline-flex items-center gap-1 border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#E11D48] hover:text-[#E11D48] transition-colors"
                          >
                            <UserMinus className="w-3 h-3" /> Demote
                          </button>
                        ) : (
                          <button
                            data-testid={`promote-${u.id}`}
                            onClick={() => setRole(u, "moderator")}
                            className="inline-flex items-center gap-1 bg-[#7C3AED] text-white font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 hover:bg-[#6D28D9] transition-colors"
                          >
                            <ShieldCheck className="w-3 h-3" /> Promote to Mod
                          </button>
                        )}
                        <AdminResetPasswordButton
                          userId={u.id}
                          username={u.username}
                          role={role}
                          testId={`users-reset-pw-${u.id}`}
                        />
                        <AdminRemovePlayerButton
                          userId={u.id}
                          username={u.username}
                          targetRole={role}
                          variant="link"
                          testId={`users-remove-${u.id}`}
                          onRemoved={(uid) => setUsers((prev) => prev.filter((x) => x.id !== uid))}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
